import * as React from 'react';
import { Attachment } from '../../models/generalMessage';
import { HTMLEditorQuill } from '../HTMLEditorQuill/HTMLEditorQuill';
import { Loader } from '../Loader/Loader';
import { FunctionComponent } from 'react';
import { LabelWithQuestionIcon } from '../LabelWithQuestionIcon/LabelWithQuestionIcon';
import { getFileName } from '../../helpers/file/file';
import { GeneralMessageVariableInsert } from '../GeneralMessageVariableInsert/GeneralMessageVariableInsert';
import { HTMLEditorQuillHandle } from '../HTMLEditorQuill/HTMLEditorQuill';
import { AppUser } from 'Src/views/App/App';

interface Props {
  user?: AppUser;
  onEmailSubjectChange: (event) => void;
  onEmailBodyChange: (editorState: any) => void;
  onSmsTextChange: (event) => void;
  onPushTitleChange: (event) => void;
  onPushBodyChange: (event) => void;
  onEmailAttachmentChange: (event) => void;
  onRemoveAttachment: (key: string) => void;
  onCloseImageError: () => void;
  onIsEmailEditorChange: (event) => void;
  onIsSmsEditorChange: (event) => void;
  onIsPushEditorChange: (event) => void;

  isEmailEditorEnabled: boolean;
  isSmsEditorEnabled: boolean;
  isPushEditorEnabled: boolean;

  isEditable?: boolean;

  messageSmsText: string;
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;

  messageAttachments: Attachment[];
  isImageError: boolean;
  isError: boolean;
  isLoadingFile: boolean;

  senderDisplayName?: string;
  onSenderDisplayNameChange?: (event) => void;

  showStudentVariables?: boolean;
  showParentVariables?: boolean;

  editorRef?: React.RefObject<HTMLEditorQuillHandle>;
  subjectInputRef?: React.RefObject<HTMLInputElement>;
  onInsertVariableToSubject?: (value: string) => void;

  fromInputRef?: React.RefObject<HTMLInputElement>;
  onInsertVariableToFrom?: (value: string) => void;
}

const SMS_CHARACTERS_LIMIT = 160;

export const GeneralMessageTextForm: FunctionComponent<Props> = props => {
  const renderAttachments = () => {
    const { isImageError, isError, messageAttachments } = props;
    const onRemoveAttachment = props.onRemoveAttachment;

    return messageAttachments.map((attachment, index) => {
      const fileName = attachment.fileName;
      const attachmentFileName = getFileName(fileName);

      let fileSize = '';
      if (typeof attachment.fileSize !== 'undefined') {
        fileSize = `${(attachment.fileSize / 1000).toFixed(0)} Kb`;
      }

      return (
        <div key={`attachment_${index}`}>
          {attachmentFileName} {fileSize}
          <button
            type="button"
            className="close"
            disabled={isImageError || isError}
            onClick={() => onRemoveAttachment(attachment.externalFileId)}
          >
            <span>&times;</span>
          </button>
        </div>
      );
    });
  };

  const renderSmsCharCounters = () => {
    const { messageSmsText } = props;
    const smsCharactersCount = messageSmsText.length;
    const smsMessagesCount = Math.ceil(smsCharactersCount / SMS_CHARACTERS_LIMIT);

    return (
      <div className="eMessageTextFormSmsEditorCounters text-secondary">
        <i>{`${smsCharactersCount} ${smsCharactersCount === 1 ? 'character' : 'characters'} have been entered.`}</i>
        <br />
        <i>{`${smsMessagesCount} ${smsMessagesCount === 1 ? 'message' : 'messages'} to be sent.`}</i>
      </div>
    );
  };

  const {
    onEmailSubjectChange,
    onEmailBodyChange,
    onSmsTextChange,
    onPushTitleChange,
    onPushBodyChange,
    messageSmsText,
    messageEmailSubject,
    messageEmailBody,
    messagePushTitle,
    messagePushBody,
    isEditable,
    onEmailAttachmentChange,
    isImageError,
    isError,
    isEmailEditorEnabled,
    isSmsEditorEnabled,
    isPushEditorEnabled,
    isLoadingFile,
    senderDisplayName,
    onSenderDisplayNameChange,
    showStudentVariables,
    showParentVariables,
    editorRef,
    subjectInputRef,
    onInsertVariableToSubject,
    fromInputRef,
    onInsertVariableToFrom
  } = props;

  const isDisabled = isEditable === false;
  const isAllEditorDisabled = !isSmsEditorEnabled && !isPushEditorEnabled && !isEmailEditorEnabled;

  return (
    <div className="bMessageTextForm">
      <div className="container-fluid pl-0">
        <div className="row">
          <div className="col-md-6">
            <div className="bMessageTextFormEmailEditor border border-primary rounded">
              <div className="form-group form-check">
                <input
                  id="messageTextFormIsEmailEditorEnabled"
                  type="checkbox"
                  className="form-check-input"
                  checked={isEmailEditorEnabled}
                  onChange={props.onIsEmailEditorChange}
                  disabled={isDisabled}
                />
                <LabelWithQuestionIcon
                  labelText="Send email"
                  hintText={
                    'Email will be sent to all users, you can choose files to attach. Please note the files ' +
                    'size is limited - you will see an error message if you try to send a file that is larger than allowed.'
                  }
                  customLabelClass="form-check-label"
                  labelHtmlFor="messageTextFormIsEmailEditorEnabled"
                />
              </div>
              {onInsertVariableToFrom && (
                <div className="form-group">
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}
                  >
                    <label style={{ margin: 0 }}>From (display name)</label>
                    <>
                      {[
                        { label: '{{schoolName}}', value: '{{schoolName}}' },
                        { label: '{{senderName}}', value: '{{senderName}}' }
                      ].map(chip => (
                        <button
                          key={chip.value}
                          type="button"
                          onClick={() => onInsertVariableToFrom(chip.value)}
                          style={{
                            backgroundColor: chip.value.startsWith('{{school') ? '#ede9fe' : '#fef3c7',
                            color: chip.value.startsWith('{{school') ? '#6d28d9' : '#92400e',
                            border: `1px solid ${chip.value.startsWith('{{school') ? '#c4b5fd' : '#fcd34d'}`,
                            borderRadius: '4px',
                            padding: '1px 7px',
                            fontSize: '11px',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            cursor: 'pointer'
                          }}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </>
                  </div>
                  <input
                    ref={fromInputRef}
                    type="text"
                    className="form-control"
                    onChange={onSenderDisplayNameChange}
                    value={senderDisplayName || ''}
                    placeholder="e.g. Mrs Smith, Headteacher"
                    disabled={isDisabled}
                  />
                  <small className="form-text text-muted">
                    If set, this name will be used as the sender name in your message. Leave blank to use the value from
                    your school settings.
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Subject</label>
                {onInsertVariableToSubject && (
                  <GeneralMessageVariableInsert
                    onInsert={onInsertVariableToSubject}
                    showStudentVariables={showStudentVariables}
                    showRecipientVariables={showParentVariables}
                    excludeVariables={['{{schoolLogo}}']}
                  />
                )}
                <input
                  ref={subjectInputRef}
                  type="text"
                  className="form-control"
                  onChange={onEmailSubjectChange}
                  value={messageEmailSubject}
                  disabled={!isEmailEditorEnabled || isDisabled}
                />
              </div>
              <div className="form-group eMessageTextEditor">
                <label>Body</label>
                {editorRef && (
                  <GeneralMessageVariableInsert
                    onInsert={value => editorRef.current?.insertVariable(value)}
                    showStudentVariables={showStudentVariables}
                    showRecipientVariables={showParentVariables}
                  />
                )}
                <HTMLEditorQuill
                  ref={editorRef}
                  user={props.user}
                  onEmailBodyChange={onEmailBodyChange}
                  value={messageEmailBody}
                  isEnabled={isEmailEditorEnabled}
                />
              </div>
              {!isDisabled && (
                <div className="form-group">
                  <label htmlFor="attachment">Attachments</label>
                  <input
                    type="file"
                    className="form-control-file"
                    id="attachment"
                    onChange={onEmailAttachmentChange}
                    disabled={isImageError || isError || isLoadingFile || isAllEditorDisabled}
                  />
                  {isLoadingFile && <Loader />}
                  {isImageError ? (
                    <div className="alert-danger">
                      Error uploading
                      <button type="button" className="close" onClick={props.onCloseImageError}>
                        <span>&times;</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
              {renderAttachments()}
            </div>
          </div>
          <div className="col-md-3">
            <div className="bMessageTextFormPushEditor border border-primary rounded">
              <div className="form-group form-check">
                <input
                  id="messageTextFormIsPushEditorEnabled"
                  type="checkbox"
                  className="form-check-input"
                  checked={isPushEditorEnabled}
                  onChange={props.onIsPushEditorChange}
                  disabled={isDisabled}
                />
                <LabelWithQuestionIcon
                  labelText=" Send push"
                  hintText={
                    'Push notifications will only be sent to users providing they have the SiT app installed on their smart device.'
                  }
                  customLabelClass="form-check-label"
                  labelHtmlFor="messageTextFormIsPushEditorEnabled"
                />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  className="form-control"
                  onChange={onPushTitleChange}
                  value={messagePushTitle}
                  disabled={!isPushEditorEnabled || isDisabled}
                />
              </div>
              <div className="form-group">
                <label>Body</label>
                <textarea
                  rows={17}
                  onChange={onPushBodyChange}
                  value={messagePushBody}
                  disabled={!isPushEditorEnabled || isDisabled}
                  className="form-control"
                />
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="bMessageTextFormSmsEditor border border-primary rounded">
              <div className="form-group form-check">
                <input
                  id="messageTextFormIsSmsEditorEnabled"
                  type="checkbox"
                  className="form-check-input"
                  checked={isSmsEditorEnabled}
                  onChange={props.onIsSmsEditorChange}
                  disabled={isDisabled}
                />
                <LabelWithQuestionIcon
                  labelText="Send sms"
                  hintText={
                    'SMS messages are charged per fragment sent, so ensure that you check how many messages ' +
                    'are being sent when composing you message. To enable you to send SMS messages this needs to be ' +
                    'included within your SiT subscription.'
                  }
                  customLabelClass="form-check-label"
                  labelHtmlFor="messageTextFormIsSmsEditorEnabled"
                />
              </div>
              <div className="form-group">
                <label>Text</label>
                <textarea
                  rows={18}
                  onChange={onSmsTextChange}
                  value={messageSmsText}
                  disabled={!isSmsEditorEnabled || isDisabled}
                  className="form-control"
                />
                {renderSmsCharCounters()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

GeneralMessageTextForm.displayName = 'GeneralMessageTextForm';
